import {
  Box,
  Card,
  CardHeader,
  Grid,
  Typography,
  useTheme,
  CircularProgress,
  Alert,
} from "@mui/material";
import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DataTableBase from "../components/Tables/dataTableBase";
import { deviceColumns } from "../components/Tables/columnDefs";
import { Person } from "@mui/icons-material";
import { dateUtils } from "../utils";
import { CONFIG } from "../config";
import PageTitle from "../components/Layout/PageTitle";
import DeleteUserButton from "../components/Buttons/DeleteUser";
import EditUserRoleButton from "../components/Buttons/EditUserRole";
import AddOrganisationButton from "../components/Buttons/AddOrganisationButton";
import { useUsers } from "../hooks/useUsers";
import { useOrganisations } from "../hooks/useOrganisations";
import { useDevices } from "../hooks/useDevices";

export default function User() {
  const params = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  const userId = parseInt(params.id);

  const {
    data: users,
    isLoading: usersLoading,
    error: usersError,
  } = useUsers();
  const {
    data: organisations,
    isLoading: orgsLoading,
    error: orgsError,
  } = useOrganisations();
  const {
    data: devices,
    isLoading: devicesLoading,
    error: devicesError,
  } = useDevices();

  const isLoading = usersLoading || orgsLoading || devicesLoading;
  const hasError = usersError || orgsError || devicesError;

  // Memoized calculations
  const { user, userOrganisations, userDevices } = useMemo(() => {
    if (!users || !organisations || !devices) {
      return { user: null, userOrganisations: [], userDevices: [] };
    }

    const foundUser = users.find((user) => user.id === userId);
    const userOrgs = organisations.filter((org) => org.owner_id === userId);
    const userDevs = devices.filter((device) =>
      userOrgs.some((org) => org.id === device.organisation_id)
    );

    return {
      user: foundUser,
      userOrganisations: userOrgs,
      userDevices: userDevs,
    };
  }, [users, organisations, devices, userId]);

  if (isLoading) {
    return (
      <Grid container spacing={1} justifyContent="center">
        <Grid item xs={12} textAlign="center">
          <CircularProgress />
        </Grid>
      </Grid>
    );
  }

  if (hasError) {
    return (
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Alert severity="error">
            Failed to load user data:{" "}
            {(usersError || orgsError || devicesError)?.message ||
              "Unknown error"}
          </Alert>
        </Grid>
      </Grid>
    );
  }

  const organisationColumns = [
    {
      name: "id",
      label: "id",
      options: {
        display: false,
      },
    },
    {
      name: "name",
      label: "Name",
      options: {
        customBodyRender: (value, tableMeta) => {
          return (
            <Typography
              sx={{
                "&:hover": {
                  cursor: "pointer",
                  textDecoration: "underline",
                },
              }}
              onClick={() => {
                navigate("/organisation/" + tableMeta.rowData[0]);
              }}
            >
              {value}
            </Typography>
          );
        },
      },
    },
    {
      name: "owner",
      label: "Owner",
      options: {
        customBodyRender: (value, tableMeta) => {
          if (!value) return "None";
          return (
            <Typography
              sx={{
                "&:hover": {
                  cursor: "pointer",
                  textDecoration: "underline",
                },
              }}
              onClick={() => {
                window.location.href = "/user/" + tableMeta.rowData[2];
              }}
            >
              {value.email}
            </Typography>
          );
        },
      },
    },
    {
      name: "active",
      label: "Status",
      options: {
        customBodyRender: (value) => {
          if (value) return "Active";
          return "Inactive";
        },
      },
    },
    {
      name: "time_created",
      label: "Date Added",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          if (!value) return "N/A";
          return dateUtils.formatDate(value);
        },
      },
    },
    {
      name: "time_updated",
      label: "Last Updated",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          if (!value) return "N/A";
          return dateUtils.formatDate(value);
        },
      },
    },
  ];

  return (
    <>
      <Grid container spacing={1}>
        <PageTitle title={user?.email || "N/A"}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {user?.role && (
              <Typography variant="body2" color="text.secondary">
                Role: {user.role === "admin" ? "Super Admin" : "User"}
              </Typography>
            )}
            <EditUserRoleButton user={user} />
            <DeleteUserButton id={params.id} />
          </Box>
        </PageTitle>
      </Grid>

      {
        // billing info
      }
      {CONFIG.billingEnabled && (
        <Box
          sx={{
            py: 2,
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={6} lg={6} xl={3}>
              <Card
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#191B1F" : "#EFEFEF",
                }}
              >
                <CardHeader
                  avatar={<Person sx={{ fontSize: "2em" }} />}
                  title="Billing Status"
                  titleTypographyProps={{
                    variant: "h6",
                    color: theme.palette.mode === "dark" ? "#8ba1b7" : "#000",
                  }}
                  action={
                    <Typography variant="h6" sx={{ py: 3 }}>
                      {user && user.stripe_billing_status
                        ? //upper case first letter
                          user.stripe_billing_status.charAt(0).toUpperCase() +
                          user.stripe_billing_status.slice(1)
                        : "N/A"}
                    </Typography>
                  }
                />
              </Card>
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6} xl={3}>
              <Card
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#191B1F" : "#EFEFEF",
                }}
              >
                <CardHeader
                  avatar={<Person sx={{ fontSize: "2em" }} />}
                  title="Device Limit"
                  titleTypographyProps={{
                    variant: "h6",
                    color: theme.palette.mode === "dark" ? "#8ba1b7" : "#000",
                  }}
                  action={
                    <Typography variant="h6" sx={{ py: 3 }}>
                      {user && user.device_limit ? user.device_limit : "N/A"}
                    </Typography>
                  }
                />
              </Card>
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6} xl={3}>
              <Card
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#191B1F" : "#EFEFEF",
                }}
              >
                <CardHeader
                  avatar={<Person sx={{ fontSize: "2em" }} />}
                  title="Customer"
                  titleTypographyProps={{
                    variant: "h6",
                    color: theme.palette.mode === "dark" ? "#8ba1b7" : "#000",
                  }}
                  action={
                    <Typography
                      variant="h6"
                      //overflows to new line
                      overflow={"hidden"}
                      sx={{
                        py: 3,
                        cursor: "pointer",
                        "&:hover": {
                          color: theme.palette.primary.main,
                          textDecoration: "underline",
                        },
                      }}
                      onClick={() => {
                        window.open(
                          "https://dashboard.stripe.com/customers/" +
                            user.stripe_customer_id,
                          "_blank"
                        );
                      }}
                    >
                      {user && user.stripe_customer_id
                        ? user.stripe_customer_id
                        : "N/A"}
                    </Typography>
                  }
                />
              </Card>
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6} xl={3}>
              <Card
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#191B1F" : "#EFEFEF",
                }}
              >
                <CardHeader
                  avatar={<Person sx={{ fontSize: "2em" }} />}
                  title="Subscription"
                  titleTypographyProps={{
                    variant: "h6",
                    color: theme.palette.mode === "dark" ? "#8ba1b7" : "#000",
                  }}
                  action={
                    <Typography
                      variant="h6"
                      //overflows to new line

                      sx={{
                        py: 3,
                        cursor: "pointer",
                        "&:hover": {
                          color: theme.palette.primary.main,
                          textDecoration: "underline",
                        },
                        overflow: "hidden",
                      }}
                      onClick={() => {
                        window.open(
                          "https://dashboard.stripe.com/subscriptions/" +
                            user.stripe_subscription_id,
                          "_blank"
                        );
                      }}
                    >
                      {user?.stripe_subscription_id || "N/A"}
                    </Typography>
                  }
                />
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}
      <Grid container spacing={1}>
        <PageTitle title="Organisations">
          <AddOrganisationButton user_id={params.id} />
        </PageTitle>
        <Grid item xs={12}>
          <DataTableBase
            columns={organisationColumns}
            data={userOrganisations}
          />
        </Grid>

        <PageTitle title="Devices"></PageTitle>
        <Grid item xs={12}>
          <DataTableBase columns={deviceColumns} data={userDevices} />
        </Grid>
      </Grid>
    </>
  );
}
